import React, { useRef, useState } from "react"
import { cn } from "~/ui-rtk/utils/tailwind-utils"

type ToggleSize = "xs" | "sm" | "md" | "lg" | "xl"

type ToggleProps = {
  id?: string
  size?: ToggleSize
  value?: boolean
  disabled?: boolean
  defaultValue?: boolean
  onChange?: (value: boolean) => void
}

const mapSize: { [key in ToggleSize]: { button: string; circle: string } } = {
  xs: {
    button: "w-10 h-5 p-0.5",
    circle: "w-3 h-3",
  },
  sm: {
    button: "w-15 h-8 p-1",
    circle: "w-5 h-5",
  },
  md: {
    button: "w-18 h-9.5 p-1",
    circle: "w-6 h-6",
  },
  lg: {
    button: "w-20 h-10.5 p-1",
    circle: "w-7.5 h-7.5",
  },
  xl: {
    button: "w-25 h-11 p-1",
    circle: "w-8.5 h-8.5",
  },
}

const Toggle: React.FC<ToggleProps> = ({
  id,
  size = "xl",
  value: valueProps,
  defaultValue,
  onChange,
  disabled = false,
}) => {
  const spanRef = useRef<HTMLSpanElement>(null)
  const isControlled = typeof valueProps !== "undefined"
  const hasDefaultValue = typeof defaultValue !== "undefined"

  const [internalValue, setInternalValue] = useState<boolean | undefined>(
    hasDefaultValue ? defaultValue : undefined,
  )
  const usedValue = isControlled ? valueProps : internalValue

  const classes = mapSize[size]

  const toggle = () => {
    if (disabled) {
      return
    }
    if (!isControlled) {
      setInternalValue(prev => !prev)
    }
    onChange?.(!usedValue)
  }

  return (
    <button
      type="button"
      id={id}
      onClick={toggle}
      disabled={disabled}
      className={cn(
        "rounded-full relative flex items-center transition-all duration-300 ease-in-out",
        classes.button,
        usedValue
          ? "bg-system-blue-500 justify-end"
          : "bg-system-blue-700 justify-start",
        disabled && "opacity-50 cursor-not-allowed",
      )}
    >
      <span
        ref={spanRef}
        className={cn(
          "rounded-full bg-white shadow-sm transition-transform duration-300 ease-in-out",
          classes.circle,
        )}
      />
    </button>
  )
}

export default Toggle
